import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { isSubscriptionNotFoundError as _isSubscriptionNotFoundError } from '@bariendo/lib/error';

const isDev = import.meta.env.DEV;

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.browserTracingIntegration({
        enableInp: isDev,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    replaysSessionSampleRate: isDev ? 1.0 : 0.05,
    replaysOnErrorSampleRate: 1.0,

    ignoreSpans: [
      { op: /^browser\.(cache|connect|DNS)$/ },
      { op: 'resource.link', name: /.+\.css(\?|$)/ },
      { op: /resource\.(link|script)/, name: /.+\.js(\?|$)/ },
      { op: /resource\.(other|img)/, name: /.+\.(png|svg|jpe?g|webp|woff2?)(\?|$)/ },
    ],

    tracesSampler: (ctx) => {
      if (isDev) {
        return ctx.inheritOrSampleWith(1.0);
      }
      return ctx.inheritOrSampleWith(0.08);
    },

    tracePropagationTargets: [
      'https://api.medplum.com',
      import.meta.env.VITE_LOCAL_SERVER,
      import.meta.env.VITE_TWILIO_WEBHOOK_URL,
    ],

    beforeSend(event, hint) {
      // Filter out expected Twilio connection errors that are service-level issues, not application bugs
      const error = hint.originalException;
      const eventMessage = event.message || '';
      const errorMessage = error && typeof error === 'object' && 'message' in error ? String(error.message) : '';
      const errorName = error && typeof error === 'object' && 'name' in error ? String(error.name) : '';
      const exceptionValue = event.exception?.values?.[0]?.value || '';
      const errorCode = error && typeof error === 'object' && 'code' in error ? String(error.code) : '';

      const isTwilioConsoleLog = () =>
        event.logger === 'console' &&
        ['[TwilioVoice]', 'ConnectionError (31005)', 'Error sent from gateway in HANGUP'].some((str) =>
          eventMessage.includes(str)
        );

      const isTwilioConnectionError = () =>
        errorName === 'ConnectionError' &&
        ['31005', 'Error sent from gateway in HANGUP'].some((str) => errorMessage.includes(str));

      const isUnauthorizedError = () =>
        errorMessage === 'Unauthorized' || exceptionValue === 'Unauthorized' || eventMessage.includes('Unauthorized');

      const isAccessTokenExpiredError = () =>
        errorName === 'AccessTokenExpired' ||
        (event.logger === 'console' && eventMessage.includes('AccessTokenExpired'));

      const isAccessTokenInvalidError = () =>
        errorName === 'AccessTokenInvalid' ||
        (event.logger === 'console' && eventMessage.includes('AccessTokenInvalid'));

      const isTwilioTransportError = () =>
        event.logger === 'console' && errorName === 'TransportError' && errorCode === '31009';

      const isSubscriptionNotFoundError = () => event.logger === 'console' && _isSubscriptionNotFoundError(error);

      if (
        isTwilioConsoleLog() ||
        isTwilioConnectionError() ||
        isUnauthorizedError() ||
        isAccessTokenExpiredError() ||
        isAccessTokenInvalidError() ||
        isTwilioTransportError() ||
        isSubscriptionNotFoundError()
      ) {
        return null;
      }

      return event;
    },
  });
}

if (import.meta.env.VITE_POSTHOG_API_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY ?? '', {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    ui_host: 'https://us.i.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
  });
}
